// src/controllers/mapController.js

import useSelectionStore from "@store/useSelectionStore";
import useMapStore from "@store/useMapStore";
import { ClickDTO } from '@models/ClickDTO';
import { ClickableType } from '@utils/enums';
import useGameStore from '@store/useGameStore';
import { getAoeInfo } from '@utils/statIcons';
import calculateAoeTiles, { getUnitsInAoe } from '@helpers/game/calculateAoeTiles';

/**
 * Función central que decide qué hacer cuando se hace clic en el mapa.
 *
 * @param {number} x - Coordenada X del tile clickeado
 * @param {number} y - Coordenada Y del tile clickeado
 * @param {object|null} object - Objeto presente en el tile (unidad, beacon, etc.)
 */
export function handleTileClick(x, y, object) {
  const { handleClick } = useSelectionStore.getState();
  const gameId = useGameStore.getState().game?.id;

  let clickDTO;

  if (object) {
    // Handle object types
    let clickType;
    if (object.type === 'unit') {
      clickType = ClickableType.UNIT;
    } else if (object.type === 'structure') {
      clickType = ClickableType.STRUCTURE;
    } else {
      throw new Error(`[mapService] Unknown object type: ${object.type}`);
    }

    clickDTO = new ClickDTO(
      clickType,
      object,
      null,
      {
        position: { x, y },
        gameId,
        team: object.team || object.teamClass
      }
    );
  } else {
    // Empty tile clicked
    clickDTO = new ClickDTO(
      ClickableType.TILE,
      { x, y },
      null,
      { gameId }
    );
  }

  // Let the selection store handle it based on current context
  handleClick(clickDTO);
}

export function resetInteractionState() {
  const selection = useSelectionStore.getState();
  selection.clearAllSelections();
}

/**
 * Handle hover over a targetable tile - shows AOE preview for ranged AOE actions.
 * @param {number} x - Tile X coordinate
 * @param {number} y - Tile Y coordinate
 */
export function handleTileHover(x, y) {
  const { targetingAction } = useSelectionStore.getState();
  const { setAoePreview } = useMapStore.getState();
  const game = useGameStore.getState().game;

  if (!targetingAction || !game) {
    return;
  }

  // Check if action has AOE targeting
  const aoeInfo = getAoeInfo(targetingAction);
  if (!aoeInfo.isAoe || !aoeInfo.aoeData) {
    return;
  }

  // Only show preview for target-origin AOE (ranged AOE)
  // Self-origin AOE is handled in ActionsPanel
  if (aoeInfo.aoeData.origin !== 'target') {
    return;
  }

  // Calculate AOE tiles centered on hovered position
  const mapBounds = { width: game.map.width, height: game.map.height };
  const aoeTiles = calculateAoeTiles(x, y, aoeInfo.aoeData, mapBounds);

  // Get units that would be affected
  const units = game.snapshots?.current?.dynamic_objects?.units || game.units || [];
  const selectedUnit = useSelectionStore.getState().getSelectedUnit();
  const actingTeamClass = selectedUnit?.teamClass || 'own';

  const affectedUnits = getUnitsInAoe(
    units,
    aoeTiles,
    aoeInfo.aoeData.filters,
    actingTeamClass
  );
  const affectedUnitIds = affectedUnits.map(u => u.id);

  setAoePreview(aoeTiles, affectedUnitIds, aoeInfo.isBeneficial);
}

/**
 * Handle mouse leaving a targetable tile - clears AOE preview.
 */
export function handleTileLeave() {
  const { clearAoePreview } = useMapStore.getState();
  clearAoePreview();
}